import type { eventWithTime } from '@rrweb/types';
import type { NodeLookup } from './CommandExecutor';
import type { NodeIdLookup } from './FieldSelectionTracker';
import { isMouseInteraction } from './pointerEvents';

export const COBROWSE_OVERLAY_CLASS = 'cobrowse-overlay';

const FLUSH_INTERVAL_MS = 50;

type RrwebRecord = (typeof import('rrweb'))['record'];

export class CobrowseRecorder {
    private bufferedEvents: eventWithTime[] = [];
    private stopRecording: (() => void) | null = null;
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private record: RrwebRecord | null = null;
    private isReplayingAgentInput = false;

    private readonly onBatch: (events: eventWithTime[]) => void;

    constructor(onBatch: (events: eventWithTime[]) => void) {
        this.onBatch = onBatch;
    }

    async start(): Promise<void> {
        if (this.stopRecording) {
            return;
        }

        const { record } = await import('rrweb');
        this.record = record;
        this.stopRecording =
            record({
                emit: (event) => {
                    if (this.isReplayingAgentInput && isMouseInteraction(event)) {
                        return;
                    }

                    this.bufferedEvents.push(event);
                },
                blockClass: COBROWSE_OVERLAY_CLASS,
                maskInputOptions: { password: true },
                inlineStylesheet: true,
                sampling: { mousemove: 50, mousemoveCallback: 50, scroll: 100 },
            }) ?? null;
        this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    }

    // The replica's cursor stands for the client: clicks the agent triggers remotely must not move it.
    withoutRecordingInteractions<Result>(action: () => Result): Result {
        this.isReplayingAgentInput = true;

        try {
            return action();
        } finally {
            this.isReplayingAgentInput = false;
        }
    }

    takeFullSnapshot(): void {
        this.record?.takeFullSnapshot(true);
        this.flush();
    }

    get nodes(): NodeLookup & NodeIdLookup {
        return {
            getNode: (nodeId) => this.record?.mirror.getNode(nodeId) ?? null,
            idOf: (node) => this.record?.mirror.getId(node) ?? -1,
        };
    }

    stop(): void {
        this.stopRecording?.();
        this.stopRecording = null;

        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }

        this.bufferedEvents = [];
    }

    private flush(): void {
        if (this.bufferedEvents.length === 0) {
            return;
        }

        const events = this.bufferedEvents;
        this.bufferedEvents = [];
        this.onBatch(events);
    }
}
